import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link as RouterLink } from "react-router-dom";
import { motion, radius } from "../../theme/tokens";
import { authTokens as t } from "../../theme/authTokens";
import { AuthBadge, AuthCard } from "./AuthLayout";
import { authButtonSx } from "./authStyles";

/**
 * Account recovery — `/forgot-password`.
 *
 * IMPORTANT: this is deliberately NOT a self-service reset, and it does not
 * pretend to be one. The portal has no recovery capability. `backend/routes/
 * api.php` exposes exactly one password route, `POST /user/change-password`
 * (`UserController::changePasswordAdmin`), and that is an authenticated
 * administrator action — there is no reset-token table, no `Password::` broker
 * and no public endpoint. So this page sends no request of any kind, and there
 * is no "Request Reset Link" button: a button that silently did nothing, or a
 * confirmation that an email had been sent when none had, would be worse than
 * the honest screen below.
 *
 * The shell, card geometry, type scale, button style and footer are shared with
 * the sign-in screen via `AuthLayout` — only this card body differs.
 *
 * If self-service recovery is ever added to the API, this component is the seam
 * to replace: drop an email field and a submit handler in, keep everything else.
 */

const STEPS = [
  {
    icon: <SupportAgentRoundedIcon aria-hidden sx={{ fontSize: 19 }} />,
    title: "Contact your administrator",
    body: "Ask your GT Brokers system administrator to reset the password on your account.",
  },
  {
    icon: <AdminPanelSettingsOutlinedIcon aria-hidden sx={{ fontSize: 19 }} />,
    title: "They set it from Setting › User",
    body: "An administrator can issue a new password for your account there, then pass it to you directly.",
  },
];

const ForgotPassword: React.FC = () => (
  <AuthCard>
    <AuthBadge />

    <Typography
      component="h1"
      sx={{
        fontSize: { xs: 22, sm: 24.5 },
        fontWeight: 700,
        letterSpacing: "-0.022em",
        color: t.text,
      }}
    >
      Forgot Password
    </Typography>
    <Typography sx={{ mt: 0.75, mb: 3, fontSize: 14, lineHeight: 1.6, color: t.muted }}>
      Passwords for this portal are issued by your administrator.
    </Typography>

    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        animation: `gtForgotIn ${motion.base}ms ${motion.easing} both`,
        "@keyframes gtForgotIn": {
          from: { opacity: 0, transform: "translateY(6px)" },
          to: { opacity: 1, transform: "none" },
        },
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      {STEPS.map((step, index) => (
        <Box
          key={step.title}
          sx={{
            display: "flex",
            gap: 1.75,
            alignItems: "flex-start",
            borderRadius: `${radius.lg}px`,
            border: `1px solid ${alpha(t.blue, 0.14)}`,
            backgroundColor: alpha(t.blue, 0.04),
            px: 2,
            py: 1.75,
          }}
        >
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: 34,
              height: 34,
              borderRadius: `${radius.md}px`,
              display: "grid",
              placeItems: "center",
              color: t.blue,
              backgroundColor: alpha(t.blue, 0.1),
              border: `1px solid ${alpha(t.blue, 0.16)}`,
            }}
          >
            {step.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 650, lineHeight: 1.45, color: t.text }}>
              <Box component="span" sx={{ opacity: 0.5, mr: 0.75 }}>
                {index + 1}.
              </Box>
              {step.title}
            </Typography>
            <Typography sx={{ mt: 0.4, fontSize: 12.5, lineHeight: 1.6, color: t.muted }}>
              {step.body}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>

    {/* Stated plainly rather than left to be discovered: nothing was sent. */}
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 2.25 }}>
      <InfoOutlinedIcon
        aria-hidden
        sx={{ fontSize: 16, color: alpha(t.muted, 0.8), mt: "1px", flexShrink: 0 }}
      />
      <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, color: alpha(t.muted, 0.95) }}>
        No reset email is sent — this portal does not offer self-service password
        recovery.
      </Typography>
    </Box>

    <Button
      component={RouterLink}
      to="/"
      variant="contained"
      fullWidth
      disableElevation
      startIcon={<ArrowBackRoundedIcon />}
      sx={[authButtonSx, { mt: 3 }]}
    >
      Back to Sign In
    </Button>

    {/* One back affordance, not two. The reference puts a "← Back to login"
        link under a "Request Reset Link" button; with no reset to request, the
        primary button IS the way back, and repeating it as a link below would
        be clutter rather than polish. */}
  </AuthCard>
);

export default ForgotPassword;
